import { ArrowUpRight, ImageUp, Sparkles } from "lucide-react";
import { useUserProfile } from "@/hooks/use-user";
import { cn } from "@/lib/utils";
import { trackPiecanHandoffClick } from "@/services/analytics";

interface PiecanNoticeProps {
  /**
   * `full` (default) — a taller banner with an icon column, used on the
   * homepage and the /assets page.
   * `compact` — a single-line variant that sits above the asset pickers /
   * composers on the generation and character forms without pushing the
   * primary content down.
   */
  variant?: "full" | "compact";
  /**
   * Analytics `source` for the "PieCan Handoff Clicked" event — distinguishes
   * which surface the click came from (home_notice, assets_notice, i2v_form,
   * image_edit_form, t2i_form, t2v_form, character_page, …).
   */
  source: string;
  className?: string;
}

/**
 * Steers upload-capable Pixelmaker users to PieCan, the sister product that
 * owns the image-upload flow. The link is a pre-signed referral URL minted
 * server-side (see `apps/backend/src/lib/piecan-referral.ts`) — the *first*
 * PieCan account this Pixelmaker user signs into via the link gets tier 1
 * automatically (PieCan dedups first-claim-wins by Pixelmaker user_id).
 *
 * Renders nothing when:
 *  - The user is not signed in / not loaded yet (no URL to link to).
 *  - `can_upload_images` is false (the notice is only for the grandfathered
 *    upload-capable set; gated users are already blocked by /upload's 403).
 *  - The backend didn't include `piecan_referral_url` (defensive — should
 *    always be present when the flag is true).
 */
export function PiecanNotice({ variant = "full", source, className }: PiecanNoticeProps) {
  const { user, isSignedIn } = useUserProfile();

  if (!isSignedIn || !user?.can_upload_images || !user.piecan_referral_url) {
    return null;
  }

  const href = user.piecan_referral_url;
  const handleClick = () => trackPiecanHandoffClick(user.user_id, source);

  if (variant === "compact") {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleClick}
        className={cn(
          "group flex items-center gap-2 rounded-lg border border-violet-400/25 bg-violet-500/[0.06] px-3 py-2 text-xs text-violet-100/90 hover:border-violet-400/50 hover:bg-violet-500/[0.1] transition-colors",
          className,
        )}
      >
        <ImageUp className="size-3.5 shrink-0 text-violet-300" />
        <span className="flex-1 min-w-0">
          Want to upload your own image? Create on{" "}
          <span className="font-semibold text-white">PieCan</span> — you'll get
          tier&nbsp;1 access automatically.
        </span>
        <ArrowUpRight className="size-3.5 shrink-0 text-violet-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
      </a>
    );
  }

  return (
    <div className={cn("w-full max-w-2xl mx-auto", className)}>
      <div className="relative">
        {/* Soft outer glow, matching the CharacterHomeCTA visual language. */}
        <div
          aria-hidden
          className="pointer-events-none absolute -inset-1 rounded-3xl bg-gradient-to-r from-violet-500/25 via-fuchsia-500/15 to-purple-500/25 blur-xl opacity-60"
        />

        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleClick}
          className="relative block rounded-2xl p-[1.5px] bg-gradient-to-r from-violet-500 via-fuchsia-500 to-purple-500 hover:from-violet-400 hover:via-fuchsia-400 hover:to-purple-400 transition-all shadow-2xl shadow-violet-500/20 group"
        >
          <div className="relative flex items-stretch gap-4 md:gap-5 rounded-2xl bg-[#0a0a0a] p-5 md:p-6 overflow-hidden">
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 bg-gradient-to-br from-violet-500/[0.06] via-transparent to-fuchsia-500/[0.06]"
            />

            <div className="hidden sm:flex shrink-0 items-center relative z-10">
              <div className="size-16 rounded-2xl bg-gradient-to-br from-violet-500/30 to-fuchsia-500/30 border border-white/[0.15] flex items-center justify-center shadow-lg shadow-violet-500/10">
                <ImageUp className="size-7 text-white" />
              </div>
            </div>

            <div className="flex-1 min-w-0 flex flex-col justify-center relative z-10">
              <div className="flex items-center gap-2 mb-1.5">
                <Sparkles className="size-3.5 text-fuchsia-300" />
                <span className="text-[11px] uppercase tracking-wider font-semibold text-fuchsia-300">
                  Partner
                </span>
              </div>
              <h3 className="text-base sm:text-lg md:text-xl font-bold text-white leading-tight">
                Want to upload your own image? Create on PieCan.
              </h3>
              <p className="text-sm text-[#aaa] mt-1.5 leading-relaxed">
                Our partner product is built for image uploads — and you'll
                land on <span className="font-medium text-white">tier&nbsp;1</span>{" "}
                automatically, no signup fees.
              </p>
            </div>

            <div className="shrink-0 self-center flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white text-black text-sm font-semibold shadow-lg group-hover:scale-[1.03] group-active:scale-[0.98] transition-transform relative z-10">
              Open PieCan
              <ArrowUpRight className="size-4" />
            </div>
          </div>
        </a>
      </div>
    </div>
  );
}
